import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import AccordionCard from 'components/Shared/AccordionCard'
import Textarea from 'components/Shared/Textarea'
import ErrorMessage from 'components/Shared/ErrorMessage'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import { useDebounce } from 'hooks'
import { fetchCustomPrompt, createCustomPrompt, updateCustomPrompt } from 'api/customPrompt'

const UPDATE_DELAY = 800

const CoCreateBriefing = ({ eventId }) => {
  const [briefing, setBriefing] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [hasGetError, setHasGetError] = useState(false)
  const { t } = useTranslation()
  const { addNotification } = useToast()

  // Only starts sending updates once the user actually edits the text -
  // otherwise the debounced effect below would also fire for the value
  // set by the initial fetch and PUT it straight back.
  const hasUserEditedRef = useRef(false)
  const debouncedBriefing = useDebounce(briefing, UPDATE_DELAY)

  useEffect(() => {
    fetchCustomPrompt(eventId).then(({ success, data }) => {
      if (success) {
        setBriefing(data?.prompt ?? '')
      } else {
        setHasGetError(true)
      }
      setIsLoading(false)
    })
  }, [eventId])

  useEffect(() => {
    if (!hasUserEditedRef.current) {
      return
    }

    const isEmpty = debouncedBriefing.trim() === ''

    const request = isEmpty
      ? updateCustomPrompt({ prompt: '', custom_prompt_enabled: 'false' }, eventId)
      : createCustomPrompt({ prompt: debouncedBriefing, custom_prompt_enabled: 'true' }, eventId)

    request.then(({ success }) => {
      if (!success) {
        addNotification({
          message: t('common.error'),
          description: t('Recommendations.coCreateBriefing.toastError.title'),
          variant: 'error',
        })
      }
    })
  }, [debouncedBriefing])

  const handleChange = (e) => {
    hasUserEditedRef.current = true
    setBriefing(e.target.value)
  }

  return  (
    <AccordionCard
        title={<p className='title'>{t('Recommendations.coCreateBriefing.title')}</p>}
        defaultOpen={false}
        className='co-create-briefing'
    >
        <div className='flex flex-column gap-3'>
            <p className='description'>{t('Recommendations.coCreateBriefing.description')}</p>

            <Textarea
                classNames="mb-0 mt-3 mt-md-0"
                placeholder={t('Recommendations.coCreateBriefing.placeholder')}
                value={briefing ?? ''}
                name="briefing"
                onChange={handleChange}
                maxLength={2500}
                showMax
                rows={5}
                required
                loading={isLoading}
                showLoadingSpinner
                disabled={isLoading || hasGetError}
            />

            {hasGetError && (
                <ErrorMessage variant="light" classNames="mt-0">
                    {t('Recommendations.coCreateBriefing.getError.title')}
                </ErrorMessage>
            )}
        </div>
    </AccordionCard>
    )
}

CoCreateBriefing.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default CoCreateBriefing
